import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import CtaPrimary from 'components/ui/CtaPrimary';
import CtaSecondary from 'components/ui/CtaSecondary';
import { colors } from 'helpers/ColorHelper';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';
import warnAnimationData from 'assets/caution.json';

import { getTranslatedString } from 'helpers/i18n';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import Lottie from 'react-lottie';

import { deleteBatchPayment } from '../../store/actions';

const DELETING_STATES = {
  confirmation: 'confirmation',
  loading: 'loading',
  success: 'success',
  error: 'error',
};

const defaultOptions = {
  loop: false,
  autoplay: true,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const StyledSecondaryCta = styled(CtaSecondary)`
  background-color: inherit;
  color: ${colors.ULTRAMARINE_BLUE};
  line-height: 36px;
  height: 36px;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  border: none;
  border-radius: 28px !important;
  padding: 1px 24px 0;
  text-align: center;
  cursor: pointer;
  &:hover {
    background-color: inherit;
  }
`;

const BatchPaymentDeleting = ({
  handleClose,
  onSubmissionComplete,
  deleteBatchPayment,
  batchPaymentId,
  batchId,
  closeDeleteDialog,
}) => {
  const [deletingState, setDeletingState] = useState(DELETING_STATES.confirmation);

  const handleDeleteBatchPayment = useCallback(
    async () => {
      setDeletingState(DELETING_STATES.loading);
      try {
        await deleteBatchPayment({
          batchId: batchId,
          batchPaymentId: batchPaymentId,
        });
        setDeletingState(DELETING_STATES.success);
        if (onSubmissionComplete) {
          onSubmissionComplete();
          closeDeleteDialog();
        }
      } catch (err) {
        setDeletingState(DELETING_STATES.error);
      }
    },
    [deleteBatchPayment, onSubmissionComplete, batchPaymentId, batchId, closeDeleteDialog]
  );

  const handleRetry = useCallback(
    () => {
      handleDeleteBatchPayment({
        batchId: batchId,
        batchPaymentId: batchPaymentId,
      });
    },
    [handleDeleteBatchPayment, batchPaymentId, batchId]
  );

  const isMobile = window.matchMedia('(max-width: 500px)').matches;
  const lottieSize = isMobile ? 80 : 150;

  if (deletingState === DELETING_STATES.confirmation) {
    return (
      <ConfirmationFace
        handleClose={handleClose}
        handleDeleteBatchPayment={handleDeleteBatchPayment}
        lottieSize={lottieSize}
      />
    );
  } else if (deletingState === DELETING_STATES.loading) {
    return <LoadingFace lottieSize={lottieSize} />;
  } else if (deletingState === DELETING_STATES.success) {
    return <SuccessFace handleClose={handleClose} lottieSize={lottieSize} />;
  } else if (deletingState === DELETING_STATES.error) {
    return (
      <ErrorFace handleRetry={handleRetry} handleClose={handleClose} lottieSize={lottieSize} />
    );
  }
};

const ConfirmationFace = ({ handleClose, handleDeleteBatchPayment, lottieSize }) => {
  const options = { ...defaultOptions, animationData: warnAnimationData };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={options} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.batch.payments.delete.confirmationHeader')}
      bodyText={getTranslatedString('landlordPortal.batch.payments.delete.confirmationBody')}
      cta={
        <CtaPrimary onClick={handleDeleteBatchPayment}>
          {getTranslatedString('landlordPortal.batch.payments.delete')}
        </CtaPrimary>
      }
      secondaryCta={
        <StyledSecondaryCta onClick={handleClose}>
          {getTranslatedString('cancel')}
        </StyledSecondaryCta>
      }
    />
  );
};

const SuccessFace = ({ handleClose, lottieSize }) => {
  const options = { ...defaultOptions, animationData: successAnimationData };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={options} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.batch.payments.delete.successHeader')}
      bodyText={getTranslatedString('landlordPortal.batch.payments.delete.successBody')}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};

const ErrorFace = ({ handleRetry, handleClose, lottieSize }) => {
  const options = { ...defaultOptions, animationData: errorAnimationData };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={options} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.batch.payments.delete.errorHeader')}
      bodyText={getTranslatedString('landlordPortal.batch.payments.delete.errorBody')}
      cta={
        <CtaPrimary onClick={handleRetry}>{getTranslatedString('globalDrawer.goBack')}</CtaPrimary>
      }
      secondaryCta={
        <StyledSecondaryCta onClick={handleClose}>
          {getTranslatedString('close')}
        </StyledSecondaryCta>
      }
    />
  );
};

const LoadingFace = ({ lottieSize }) => {
  const options = {
    ...defaultOptions,
    animationData: loadingAnimationData,
    loop: true,
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={options} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.batch.payments.delete.loadingHeader')}
      bodyText={getTranslatedString('landlordPortal.batch.payments.delete.loadingBody')}
    />
  );
};

const mapStoreToProps = store => ({});

const mapDispatchToProps = { deleteBatchPayment };

export default connect(mapStoreToProps, mapDispatchToProps)(BatchPaymentDeleting);
